import { useCallback, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";
import { Menu } from "lucide-react";

echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  MarkLineComponent,
  MarkPointComponent,
  CanvasRenderer,
]);

type Props = {
  status: "idle" | "running" | "done";
  scenario: "baseline" | "sentiment" | "ai_baseline" | "ai_sentiment";
  data?: any;
  symbol?: string;
  durationMs?: number;
}

type BacktestChartData = ReturnType<typeof buildMockSeries>;

function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildMockSeries(
  seed = 20250215,
  opts: { drift?: number; vol?: number } = {}
) {
  const rand = mulberry32(seed);
  const drift = opts.drift ?? 0;
  const vol = opts.vol ?? 1;

  const dataCount = 300;
  const startDate = new Date("2025-02-15").getTime();
  const oneDay = 24 * 3600 * 1000;

  const floatingData: Array<[number, number]> = [];
  const benchmarkData: Array<[number, number]> = [];
  const periodicData: Array<[number, number]> = [];
  const longData: Array<[number, number]> = [];
  const shortData: Array<[number, number]> = [];
  const volumeData: Array<[number, number]> = [];
  const utilData: Array<[number, number]> = [];

  let currentFloating = 0;
  let currentBenchmark = 0;
  let currentPos = 0;
  let currentUtil = 0.5;

  for (let i = 0; i < dataCount; i++) {
    const time = startDate + i * oneDay;

    const floatChange = ((rand() - 0.48) * 1500 + drift * 1500) * vol;
    const benchChange = (rand() - 0.5) * 1000;
    currentFloating += floatChange;
    currentBenchmark += benchChange;

    if (i > 220 && i < 240) currentFloating += 2000;
    if (i > 240 && i < 250) currentFloating -= 2500;

    floatingData.push([time, currentFloating]);
    benchmarkData.push([time, currentBenchmark]);

    const periodic = (rand() - 0.5) * 4000 * vol + floatChange * 1.5;
    periodicData.push([time, periodic]);

    if (rand() > 0.92) {
      currentPos = rand() > 0.5 ? 1 : -1;
    } else if (rand() > 0.95) {
      currentPos = 0;
    }
    longData.push([time, currentPos === 1 ? 1 : 0]);
    shortData.push([time, currentPos === -1 ? -1 : 0]);

    const volume = rand() > 0.85 ? rand() * 1.8 : 0;
    volumeData.push([time, volume]);

    if (rand() > 0.85) {
      currentUtil = rand() * 0.8 + 0.1;
    }
    utilData.push([time, currentUtil]);
  }

  return {
    floatingData,
    benchmarkData,
    periodicData,
    longData,
    shortData,
    volumeData,
    utilData,
  };
}

function formatMonthYear(value: number) {
  const d = new Date(value);
  const month = d.toLocaleString("en-US", { month: "short" });
  const yy = String(d.getFullYear()).slice(-2);
  return `${month} '${yy}`;
}

function buildBacktestSeriesUpdates(
  chartData: BacktestChartData,
  baselineData: BacktestChartData | null,
  points: number
) {
  const n = Math.max(0, Math.min(points, chartData.floatingData.length));
  const slice = <T,>(arr: T[]) => arr.slice(0, Math.min(arr.length, n));

  return [
    { data: slice(chartData.floatingData) },
    { data: slice(chartData.benchmarkData) },
    { data: slice(chartData.periodicData) },
    { data: slice(chartData.longData) },
    { data: slice(chartData.shortData) },
    { data: slice(chartData.volumeData) },
    { data: slice(chartData.utilData) },
    ...(baselineData ? [{ data: slice(baselineData.floatingData) }] : []),
  ];
}

function buildBacktestChartOption(
  chartData: BacktestChartData,
  baselineData: BacktestChartData | null,
  pointsLimit?: number
): EChartsOption {
  const n =
    typeof pointsLimit === "number"
      ? Math.max(0, Math.min(pointsLimit, chartData.floatingData.length))
      : chartData.floatingData.length;
  const slice = <T,>(arr: T[]) => arr.slice(0, Math.min(arr.length, n));

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line", lineStyle: { color: "#aaa" } },
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderColor: "#ccc",
      textStyle: { color: "#333", fontSize: 12 },
    },
    legend: {
      show: baselineData !== null,
      top: 4,
      right: "5%",
      textStyle: { fontSize: 11, color: "#666" },
      data: baselineData !== null ? ["Previous Version"] : [],
    },
    axisPointer: {
      link: [{ xAxisIndex: "all" }],
    },
    dataZoom: [
      { type: "inside", xAxisIndex: [0, 1, 2, 3] },
      {
        type: "slider",
        xAxisIndex: [0, 1, 2, 3],
        bottom: 0,
        height: 20,
        borderColor: "transparent",
        backgroundColor: "#e2e8f0",
        fillerColor: "rgba(186, 206, 235, 0.5)",
        handleSize: "100%",
        showDataShadow: false,
        showDetail: false,
      },
    ],
    grid: [
      { top: "2%", height: "40%", left: "3%", right: "5%" },
      { top: "45%", height: "12%", left: "3%", right: "5%" },
      { top: "60%", height: "12%", left: "3%", right: "5%" },
      { top: "75%", height: "12%", left: "3%", right: "5%" },
    ],
    xAxis: [
      {
        type: "time",
        gridIndex: 0,
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: "#ccc" } },
      },
      {
        type: "time",
        gridIndex: 1,
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: "#ccc" } },
      },
      {
        type: "time",
        gridIndex: 2,
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: "#ccc" } },
      },
      {
        type: "time",
        gridIndex: 3,
        axisLabel: { formatter: formatMonthYear, color: "#999", fontSize: 11 },
        axisTick: { show: true, lineStyle: { color: "#ccc" } },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: "#ccc" } },
      },
    ],
    yAxis: [
      {
        gridIndex: 0,
        position: "right",
        name: "Floating PnL",
        splitLine: { lineStyle: { color: "#f0f0f0" } },
        axisLabel: { formatter: (val: number) => `${val / 1000}k`, color: "#999", fontSize: 10 },
        nameTextStyle: { padding: [0, 0, 0, 25], color: "#666" },
        nameRotate: -90,
        nameLocation: "middle",
      },
      {
        gridIndex: 1,
        position: "right",
        name: "Period PnL",
        splitLine: { show: true, lineStyle: { color: "#f0f0f0" } },
        axisLabel: {
          formatter: (val: number) => `${(val / 1000).toFixed(0)}k`,
          color: "#999",
          fontSize: 10,
        },
        nameTextStyle: { padding: [0, 0, 0, 25], color: "#666" },
        nameRotate: -90,
        nameLocation: "middle",
      },
      {
        gridIndex: 2,
        position: "right",
        name: "Volume / Position",
        min: -1.5,
        max: 2,
        splitLine: { show: true, lineStyle: { color: "#f0f0f0" } },
        axisLabel: { color: "#999", fontSize: 10 },
        nameTextStyle: { padding: [0, 0, 0, 25], color: "#666" },
        nameRotate: -90,
        nameLocation: "middle",
      },
      {
        gridIndex: 3,
        position: "right",
        name: "Utilization",
        min: 0,
        max: 1,
        splitLine: { show: false },
        axisLabel: { formatter: (val: number) => String(val), color: "#999", fontSize: 10 },
        nameTextStyle: { padding: [0, 0, 0, 25], color: "#666" },
        nameRotate: -90,
        nameLocation: "middle",
      },
    ],
    series: [
      {
        name: "Floating PnL",
        type: "line",
        xAxisIndex: 0,
        yAxisIndex: 0,
        showSymbol: false,
        itemStyle: { color: "#3b82f6" },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(59, 130, 246, 0.2)" },
            { offset: 1, color: "rgba(59, 130, 246, 0)" },
          ]),
        },
        data: slice(chartData.floatingData),
        markLine: {
          symbol: ["none", "none"],
          data: [{ yAxis: 0 }],
          lineStyle: { color: "#333", width: 1, type: "solid" },
          label: { show: false },
        },
        markPoint: {
          data: [
            {
              type: "max",
              name: "Max",
              symbol: "circle",
              symbolSize: 10,
              itemStyle: { color: "#10b981", borderColor: "#fff", borderWidth: 2 },
            },
            {
              type: "min",
              name: "Min",
              symbol: "circle",
              symbolSize: 10,
              itemStyle: { color: "#ef4444", borderColor: "#fff", borderWidth: 2 },
            },
          ],
        },
      },
      {
        name: "Benchmark Return",
        type: "line",
        xAxisIndex: 0,
        yAxisIndex: 0,
        showSymbol: false,
        itemStyle: { color: "#c24b4b" },
        lineStyle: { width: 1 },
        data: slice(chartData.benchmarkData),
      },
      {
        name: "Period PnL",
        type: "bar",
        xAxisIndex: 1,
        yAxisIndex: 1,
        itemStyle: {
          color: (params: any) => (params.value[1] >= 0 ? "#84cc6d" : "#e66555"),
        },
        data: slice(chartData.periodicData),
        markLine: {
          symbol: ["none", "none"],
          data: [{ yAxis: 0 }],
          lineStyle: { color: "#e5e7eb", width: 1, type: "solid" },
          label: { show: false },
        },
      },
      {
        name: "Long",
        type: "line",
        step: "end",
        xAxisIndex: 2,
        yAxisIndex: 2,
        showSymbol: false,
        lineStyle: { width: 0 },
        areaStyle: { color: "#a8f0a8", opacity: 1 },
        data: slice(chartData.longData),
      },
      {
        name: "Short",
        type: "line",
        step: "end",
        xAxisIndex: 2,
        yAxisIndex: 2,
        showSymbol: false,
        lineStyle: { width: 0 },
        areaStyle: { color: "#fcd38d", opacity: 1 },
        data: slice(chartData.shortData),
      },
      {
        name: "Volume",
        type: "bar",
        xAxisIndex: 2,
        yAxisIndex: 2,
        barWidth: 2,
        itemStyle: { color: "#333" },
        data: slice(chartData.volumeData),
        markLine: {
          symbol: ["none", "none"],
          data: [{ yAxis: 0 }],
          lineStyle: { color: "#e5e7eb", width: 1, type: "solid" },
          label: { show: false },
        },
      },
      {
        name: "Utilization",
        type: "line",
        step: "end",
        xAxisIndex: 3,
        yAxisIndex: 3,
        showSymbol: false,
        lineStyle: { width: 1.5 },
        itemStyle: { color: "#8c6b3e" },
        data: slice(chartData.utilData),
      },
      ...(baselineData !== null
        ? [
            {
              name: "Previous Version",
              type: "line" as const,
              xAxisIndex: 0,
              yAxisIndex: 0,
              showSymbol: false,
              itemStyle: { color: "#f59e0b" },
              lineStyle: { color: "#f59e0b", width: 1.5, type: "dashed" as const },
              data: slice(baselineData.floatingData),
              z: 1,
            },
          ]
        : []),
    ],
  };
}

function useBacktestChartModel(
  scenario: "baseline" | "sentiment" | "ai_baseline" | "ai_sentiment",
  data?: any
): {
  chartData: BacktestChartData;
  baselineData: BacktestChartData | null;
  baseOption: EChartsOption;
  fullOption: EChartsOption;
} {
  const chartData = useMemo(() => {
    if (data?.chartData) return data.chartData;

    if (scenario === "sentiment")
      return buildMockSeries(20250301, { drift: 0.18, vol: 0.85 });
    if (scenario === "ai_baseline")
      return buildMockSeries(20250401, { drift: 0.05, vol: 0.9 });
    if (scenario === "ai_sentiment")
      return buildMockSeries(20250501, { drift: 0.25, vol: 0.7 });
    return buildMockSeries(20250215, { drift: 0, vol: 1 });
  }, [data, scenario]);

  // When showing the sentiment (second) backtest, also compute the baseline data for comparison
  const baselineData = useMemo(() => {
    if (scenario === "sentiment") return buildMockSeries(20250215, { drift: 0, vol: 1 });
    if (scenario === "ai_sentiment") return buildMockSeries(20250401, { drift: 0.05, vol: 0.9 });
    return null;
  }, [scenario]);

  const baseOption = useMemo(
    () => buildBacktestChartOption(chartData, baselineData, 0),
    [baselineData, chartData]
  );
  const fullOption = useMemo(
    () => buildBacktestChartOption(chartData, baselineData),
    [baselineData, chartData]
  );

  return { chartData, baselineData, baseOption, fullOption };
}

export function StrategiesBacktestView({
  status,
  scenario,
  data,
  symbol = "Binance.BTC_USDT",
  durationMs = 5000,
}: Props) {
  const chartDomRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.EChartsType | null>(null);
  const { chartData, baselineData, baseOption, fullOption } = useBacktestChartModel(scenario, data);
  const totalPoints = chartData.floatingData.length;

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const rafRef = useRef<number | null>(null);
  const lastPointsRef = useRef<number>(-1);

  const cancelProgressiveDraw = useCallback(() => {
    if (rafRef.current === null) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const isRunning = status === "running";
  const panelBgClass = isRunning ? "bg-white/70 backdrop-blur-md" : "bg-white";

  useEffect(() => {
    if (status === "idle") {
      cancelProgressiveDraw();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
      return;
    }

    const dom = chartDomRef.current;
    if (!dom) return;

    if (chartRef.current) return;

    const instance = echarts.init(dom, undefined, { renderer: "canvas" });
    chartRef.current = instance;
    instance.setOption(baseOption);

    requestAnimationFrame(() => {
      instance.resize();
    });

    const ro =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            instance.resize();
          });
    ro?.observe(dom);

    const onResize = () => instance.resize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelProgressiveDraw();
      ro?.disconnect();
      window.removeEventListener("resize", onResize);
      instance.dispose();
      chartRef.current = null;
    };
  }, [baseOption, cancelProgressiveDraw, status]);

  useEffect(() => {
    const instance = chartRef.current;
    if (!instance) return;

    cancelProgressiveDraw();

    if (status === "running") {
      const DRAW_MS = Math.max(300, durationMs);
      const start = performance.now();
      lastPointsRef.current = -1;

      instance.setOption(baseOption, { notMerge: true, lazyUpdate: true });

      const step = (now: number) => {
        if (statusRef.current !== "running") return;

        const t = Math.max(0, Math.min(1, (now - start) / DRAW_MS));
        const nextPoints = Math.max(2, Math.floor(t * totalPoints));

        if (nextPoints !== lastPointsRef.current) {
          instance.setOption(
            { series: buildBacktestSeriesUpdates(chartData, baselineData, nextPoints) as any },
            { notMerge: false, lazyUpdate: true }
          );
          lastPointsRef.current = nextPoints;
        }

        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        }
      };

      rafRef.current = requestAnimationFrame(step);
      return;
    }

    if (status === "done") {
      instance.setOption(fullOption, { notMerge: true, lazyUpdate: true });
      return;
    }
  }, [
    baseOption,
    baselineData,
    cancelProgressiveDraw,
    chartData,
    durationMs,
    fullOption,
    status,
    totalPoints,
  ]);

  const stats =
    data?.metrics ??
    (scenario === "sentiment"
      ? {
          initialNav: "50,000",
          totalReturn: "68.214%",
          annualized: "69.003%",
          sharpe: "0.041",
          volatility: "1620.412%",
          maxDrawdown: "21.378%",
        }
      : scenario === "ai_baseline"
      ? {
          initialNav: "50,000",
          totalReturn: "84.532%",
          annualized: "85.104%",
          sharpe: "0.098",
          volatility: "1340.207%",
          maxDrawdown: "18.231%",
        }
      : scenario === "ai_sentiment"
      ? {
          initialNav: "50,000",
          totalReturn: "127.841%",
          annualized: "128.953%",
          sharpe: "0.142",
          volatility: "1180.433%",
          maxDrawdown: "12.567%",
        }
      : {
          initialNav: "50,000",
          totalReturn: "51.542%",
          annualized: "52.065%",
          sharpe: "0.026",
          volatility: "1905.009%",
          maxDrawdown: "29.605%",
        });

  return (
    <main className={`flex-1 h-full relative overflow-hidden shadow-inner flex flex-col min-h-0 ${panelBgClass}`}>
      <div
        className={`flex flex-row items-stretch justify-between border-b border-gray-200 ${panelBgClass}`}
      >
        {/* Left side - vertical stack */}
        <div className="flex flex-col pl-[280px] sm:pl-[300px] pr-4">
          <div className="flex items-center px-0 py-3">
            <span className="text-[15px] font-bold text-gray-900">Performance Overview</span>
          </div>
          <div className="">
            <div className="inline-block px-4 py-2 border-b-2 border-teal-500 text-teal-600 font-medium text-sm">
              {symbol}
            </div>
          </div>
        </div>

        {/* Right side - metrics table, spans full height */}
        <div className="flex flex-1 justify-end items-center px-4 overflow-x-auto py-2">
          <div className="flex gap-1.5 min-w-max">
            <div className="flex flex-col justify-center px-2.5 bg-slate-50 border border-gray-100 rounded min-w-[80px]">
              <span className="text-[9px] uppercase text-gray-500 tracking-wider mb-0.5">Initial NAV</span>
              <span className="font-mono tabular-nums text-[13px] font-bold text-gray-800 leading-none">{stats.initialNav}</span>
            </div>
            <div className="flex flex-col justify-center px-2.5 bg-slate-50 border border-gray-100 rounded min-w-[80px] py-1.5">
              <span className="text-[9px] uppercase text-gray-500 tracking-wider mb-0.5">Total Return</span>
              <span className="font-mono tabular-nums text-[13px] font-bold text-teal-600 leading-none">{stats.totalReturn}</span>
            </div>
            <div className="flex flex-col justify-center px-2.5 bg-slate-50 border border-gray-100 rounded min-w-[80px] py-1.5">
              <span className="text-[9px] uppercase text-gray-500 tracking-wider mb-0.5">Annualized</span>
              <span className="font-mono tabular-nums text-[13px] font-bold text-teal-600 leading-none">{stats.annualized}</span>
            </div>
            <div className="flex flex-col justify-center px-2.5 bg-slate-50 border border-gray-100 rounded min-w-[80px] py-1.5">
              <span className="text-[9px] uppercase text-gray-500 tracking-wider mb-0.5">Sharpe</span>
              <span className="font-mono tabular-nums text-[13px] font-bold text-gray-800 leading-none">{stats.sharpe}</span>
            </div>
            <div className="flex flex-col justify-center px-2.5 bg-slate-50 border border-gray-100 rounded min-w-[80px] py-1.5">
              <span className="text-[9px] uppercase text-gray-500 tracking-wider mb-0.5">Volatility</span>
              <span className="font-mono tabular-nums text-[13px] font-bold text-gray-800 leading-none">{stats.volatility}</span>
            </div>
            <div className="flex flex-col justify-center px-2.5 bg-slate-50 border border-gray-100 rounded min-w-[80px] py-1.5">
              <span className="text-[9px] uppercase text-gray-500 tracking-wider mb-0.5">Max DD</span>
              <span className="font-mono tabular-nums text-[13px] font-bold text-rose-500 leading-none">{stats.maxDrawdown}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center px-4 py-3 text-sm text-gray-600">
        <div className="flex items-center space-x-1 hidden md:flex">
          <span className="mr-2 text-gray-500 text-xs">Zoom</span>
          {[
            { id: "1h", active: false },
            { id: "3h", active: false },
            { id: "8h", active: false },
            { id: "12h", active: false },
            { id: "24h", active: false },
            { id: "All", active: true },
          ].map((z) => (
            <button
              key={z.id}
              type="button"
              className={
                z.active
                  ? "px-2 py-0.5 bg-blue-100 text-blue-700 font-bold rounded text-xs"
                  : "px-2 py-0.5 bg-gray-100 text-gray-400 rounded text-xs hover:bg-gray-200"
              }
            >
              {z.id}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-4 ml-auto md:ml-0">
          <button type="button" className="hover:text-gray-900 cursor-pointer text-[13px]">
            Fullscreen
          </button>
          <Menu className="w-5 h-5 text-gray-600 cursor-pointer" aria-hidden="true" />
        </div>
      </div>

      <div className="flex items-center space-x-1 px-4 pb-2 md:hidden">
        <span className="mr-2 text-gray-500 text-xs">Zoom</span>
        {[
          { id: "1h", active: false },
          { id: "3h", active: false },
          { id: "All", active: true },
        ].map((z) => (
          <button
            key={z.id}
            type="button"
            className={
              z.active
                ? "px-2 py-0.5 bg-blue-100 text-blue-700 font-bold rounded text-xs"
                : "px-2 py-0.5 bg-gray-100 text-gray-400 rounded text-xs hover:bg-gray-200"
            }
          >
            {z.id}
          </button>
        ))}
      </div>

      <div className="relative flex-1 w-full min-h-[500px]">
        {status === "running" && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/30 backdrop-blur-[1px]">
            <div className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg shadow-sm">
              Running backtest…
            </div>
          </div>
        )}

        {status === "idle" ? (
          <div className="absolute inset-0 bg-white" />
        ) : (
          <div className="absolute inset-0">
            <div ref={chartDomRef} className="h-full w-full" />
          </div>
        )}
      </div>
    </main>
  );
}
